import { useQueryClient } from '@tanstack/react-query'
import { Ban, BookOpen, Bot, Pin, ShieldCheck, TriangleAlert } from 'lucide-react'
import { useId, useState } from 'react'
import type { AdminListingRow, NeedsOwnerRow } from '@/api/maker.ts'
import { GroupLabel } from '@/components/groups/GroupLabel.tsx'
import { type OwnerChoice, OwnerPicker, ownerRefOf } from '@/components/groups/OwnerPicker.tsx'
import { riseDelay, SectionHead, StudioRow } from '@/components/ui/admin-chrome.tsx'
import { Badge, Button, EmptyState, Input, Panel, Textarea } from '@/components/ui/primitives.tsx'
import { useSession } from '@/hooks/use-api.ts'
import { useMoveKnowledgeBase } from '@/hooks/use-knowledge-bases.ts'
import {
  useAdminListings,
  useFeatureListing,
  useNeedsOwner,
  useSetListingOfficial,
  useSuspendListing,
  useTransferListing,
  useUnsuspendListing
} from '@/hooks/use-maker.ts'
import { describeApiError, useI18n, useT } from '@/i18n/index.ts'

/**
 * Curation: featured order, the Official badge, suspension, and the needs-owner queue.
 *
 * The queue holds agents and knowledge bases alike (PRD-26 GRP-030): anything whose personal owner
 * has left, or whose owning group has no active manager. It is answered with the owner picker —
 * a person or a group, found by name — never with an id typed into a field.
 *
 * Everything on this panel is unreachable from a maker request by construction — none of
 * these fields exist in `MakerAgentUpsert` — so the studio is where they live, in the
 * platform's own vocabulary. Suspension takes two reasons on purpose: the consumer-safe one
 * that appears on the listing, and the full one that reaches the owner and the audit trail
 * (AGM-047).
 *
 * A row's stored identifier is rendered as its own element, alone, with nothing else in the
 * node. That is how the browser suite addresses one listing out of a list where two agents
 * can share a display name, and joining it to the owner line to save a row would make the
 * curation tests unable to name what they are about to suspend.
 */
export const CurationPanel = ({ enabled }: { enabled: boolean }) => {
  // The needs-owner queue's words, read in the body rather than captured at module scope —
  // `const o = curationStrings.needsOwner` beside the import was the pattern that renders
  // the previous language after a locale switch with nothing failing (PRD-27 F-3, LOC-012).
  const words = useT()
  const strings = words.admin.studio.curation
  const o = words.admin.capabilities.listings.needsOwner
  const listings = useAdminListings(enabled)
  const needsOwner = useNeedsOwner(enabled)
  const [suspending, setSuspending] = useState<AdminListingRow | null>(null)
  const [ownerMoved, setOwnerMoved] = useState<string | null>(null)

  return (
    <div className="space-y-7">
      <section>
        <SectionHead title={strings.title} lead={strings.lead} />

        {listings.isLoading && <p className="mt-3 text-[13px] text-fg-subtle">{strings.loading}</p>}

        {listings.isError && (
          <div className="mt-3">
            <EmptyState title={strings.unavailable} body={strings.unavailableBody} />
          </div>
        )}

        {listings.data?.items.length === 0 && (
          <div className="mt-3">
            <EmptyState title={strings.empty} body={strings.emptyBody} />
          </div>
        )}

        <ul className="mt-4 space-y-2.5">
          {listings.data?.items.map((listing, index) => (
            <li key={listing.agentId}>
              <ListingRow listing={listing} index={index} onSuspend={() => setSuspending(listing)} />
            </li>
          ))}
        </ul>
      </section>

      {suspending && <SuspendForm listing={suspending} onDone={() => setSuspending(null)} />}

      <section>
        <SectionHead title={strings.needsOwnerTitle} lead={strings.needsOwnerLead} />
        <p className="mt-1.5 text-[12.5px] leading-relaxed text-fg-subtle">{o.hint}</p>

        {/* Mounted empty, and outside the list: the row a move answers usually leaves the list when it
            is read again, and the sentence has to be announced and stay after it has gone. */}
        <p role="status" className="mt-2.5 text-[12.5px] text-grounded">
          {ownerMoved}
        </p>

        {needsOwner.isLoading && <p className="mt-2.5 text-[13px] text-fg-subtle">{o.loading}</p>}
        {needsOwner.isError && <p className="mt-2.5 text-[13px] text-alert">{o.failed}</p>}
        {needsOwner.data?.items.length === 0 && <p className="mt-2.5 text-[13px] text-fg-subtle">{o.empty}</p>}

        <ul className="mt-4 space-y-2.5">
          {needsOwner.data?.items.map((row, index) => (
            <li key={`${row.kind}:${row.id}`}>
              <NeedsOwnerItem row={row} index={index} onMoved={setOwnerMoved} />
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}

const ListingRow = ({
  listing,
  index,
  onSuspend
}: {
  listing: AdminListingRow
  index: number
  onSuspend: () => void
}) => {
  const fieldId = useId()
  // Inside the component body, never at module scope: a formatter captured once at import time
  // keeps the locale it was read in and then quietly renders the wrong one (PRD-27 F-3).
  const { formatAgo, t: messages } = useI18n()
  const strings = messages.admin.studio.curation
  const rows = messages.admin.capabilities.listings
  const [rank, setRank] = useState(listing.featuredRank === null ? '' : String(listing.featuredRank))
  const feature = useFeatureListing()
  const official = useSetListingOfficial()
  const unsuspend = useUnsuspendListing()

  return (
    <StudioRow style={riseDelay(index)}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
        <h3 className="min-w-0 truncate text-[14px]">{listing.displayName}</h3>
        <span className="readout text-fg-subtle">{listing.agentId}</span>
        <Badge tone={listing.state === 'published' ? 'grounded' : listing.state === 'suspended' ? 'alert' : 'neutral'}>
          {rows.state[listing.state]}
        </Badge>
        {listing.official && (
          <Badge tone="grounded">
            <ShieldCheck size={10} strokeWidth={2.6} aria-hidden />
            official
          </Badge>
        )}
        {listing.health === 'attention' && (
          <Badge tone="alert">
            <TriangleAlert size={10} strokeWidth={2.6} aria-hidden />
            health
          </Badge>
        )}
      </div>

      <p className="readout mt-1 text-fg-subtle">
        {listing.ownerName} · {listing.category || 'uncategorised'} · {rows.audience[listing.audienceKind]} ·{' '}
        {formatAgo(listing.updatedAt)}
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-rule pt-3">
        <label htmlFor={`${fieldId}-rank`} className="eyebrow">
          {strings.featureRank}
        </label>
        {/* The field primitive is `w-full`; the width belongs on a wrapper rather than on a
            competing utility whose position in Tailwind's output decides the winner. */}
        <div className="mr-1 w-16">
          <Input
            id={`${fieldId}-rank`}
            type="number"
            min={1}
            value={rank}
            aria-label={`${strings.featureRank} for ${listing.displayName}`}
            className="readout h-[30px] text-[12.5px]"
            onChange={(event) => setRank(event.target.value)}
          />
        </div>
        <Button
          size="sm"
          icon={<Pin size={12} />}
          disabled={feature.isPending}
          onClick={() => feature.mutate({ agentId: listing.agentId, rank: rank.trim() === '' ? null : Number(rank) })}
        >
          {rank.trim() === '' ? strings.featureClear : 'Pin'}
        </Button>
        <Button
          size="sm"
          icon={<ShieldCheck size={12} />}
          disabled={official.isPending}
          onClick={() => official.mutate({ agentId: listing.agentId, value: !listing.official })}
        >
          {listing.official ? strings.officialOff : strings.officialOn}
        </Button>
        {listing.state === 'suspended' ? (
          <Button size="sm" disabled={unsuspend.isPending} onClick={() => unsuspend.mutate(listing.agentId)}>
            {strings.unsuspend}
          </Button>
        ) : (
          <Button size="sm" variant="danger" icon={<Ban size={12} />} onClick={onSuspend}>
            {strings.suspend}
          </Button>
        )}
      </div>

      {(feature.isError || official.isError || unsuspend.isError) && (
        <p role="alert" className="mt-2 text-[12.5px] text-alert">
          {strings.failed}
        </p>
      )}
    </StudioRow>
  )
}

/**
 * Suspension, with the two reasons side by side.
 *
 * Drawn in the alert channel rather than as another neutral card: this is the one control in
 * the studio that stops every conversation with an agent platform-wide within a minute, and
 * it should not look like the form above it.
 */
const SuspendForm = ({ listing, onDone }: { listing: AdminListingRow; onDone: () => void }) => {
  const messages = useT()
  const strings = messages.admin.studio.curation
  const [reasonPublic, setReasonPublic] = useState('')
  const [reasonFull, setReasonFull] = useState('')
  const [replacement, setReplacement] = useState('')
  const suspend = useSuspendListing()

  return (
    <Panel solid className="fade-in border-alert/40 px-4 py-4">
      <h3 className="flex items-center gap-2 text-[14.5px]">
        <Ban size={14} className="text-alert" aria-hidden />
        {strings.suspendTitle(listing.displayName)}
      </h3>
      <p className="mt-1.5 text-[12.5px] leading-relaxed text-fg-muted">{strings.suspendLead}</p>

      <div className="mt-3.5 space-y-3">
        <div>
          <label htmlFor="suspend-public" className="eyebrow mb-1 block">
            {strings.reasonPublic}
          </label>
          <Input
            id="suspend-public"
            value={reasonPublic}
            placeholder={strings.reasonPublicPlaceholder}
            onChange={(event) => setReasonPublic(event.target.value)}
          />
        </div>
        <div>
          <label htmlFor="suspend-full" className="eyebrow mb-1 block">
            {strings.reasonFull}
          </label>
          <Textarea
            id="suspend-full"
            rows={3}
            value={reasonFull}
            placeholder={strings.reasonFullPlaceholder}
            onChange={(event) => setReasonFull(event.target.value)}
          />
        </div>
        <div>
          <label htmlFor="suspend-replacement" className="eyebrow mb-1 block">
            {strings.replacement}
          </label>
          <Input
            id="suspend-replacement"
            value={replacement}
            placeholder={strings.replacementPlaceholder}
            className="readout"
            onChange={(event) => setReplacement(event.target.value)}
          />
        </div>
      </div>

      {suspend.isError && (
        <p role="alert" className="mt-2.5 text-[12.5px] leading-relaxed text-alert">
          {strings.failed} {describeApiError(messages, suspend.error)}
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          variant="danger"
          size="sm"
          disabled={suspend.isPending || reasonPublic.trim() === '' || reasonFull.trim() === ''}
          onClick={() =>
            suspend.mutate(
              {
                agentId: listing.agentId,
                reasonPublic: reasonPublic.trim(),
                reasonFull: reasonFull.trim(),
                replacementAgentId: replacement.trim() === '' ? null : replacement.trim()
              },
              { onSuccess: onDone }
            )
          }
        >
          {strings.confirmSuspend}
        </Button>
        <Button variant="ghost" size="sm" onClick={onDone}>
          {strings.cancel}
        </Button>
      </div>
    </Panel>
  )
}

/**
 * One thing without an owner, and the move that gives it one.
 *
 * The owner line says why it is here in words — a person who left, or a group nobody manages —
 * because "needs an owner" alone leaves an administrator to work out which of the two to fix: a
 * group with no managers may want a manager appointed on the Groups page rather than a new owner.
 */
const NeedsOwnerItem = ({
  row,
  index,
  onMoved
}: {
  row: NeedsOwnerRow
  index: number
  /** Said by the section, not the row: the row leaves the list once it has an owner. */
  onMoved: (sentence: string) => void
}) => {
  const session = useSession()
  const user = session.data?.user
  const { formatAgo, t: messages } = useI18n()
  const o = messages.admin.capabilities.listings.needsOwner
  const client = useQueryClient()
  const moveAgent = useTransferListing()
  const moveBase = useMoveKnowledgeBase()
  const [choosing, setChoosing] = useState(false)
  const [choice, setChoice] = useState<OwnerChoice | null>(null)

  const pending = moveAgent.isPending || moveBase.isPending
  const error = moveAgent.error ?? moveBase.error
  const Icon = row.kind === 'agent' ? Bot : BookOpen
  const current =
    row.owner.kind === 'user'
      ? { kind: 'user' as const, id: row.owner.id }
      : { kind: 'group' as const, id: row.owner.group.id }

  const submit = () => {
    if (!choice) return
    const to = ownerRefOf(choice)
    const done = () => {
      onMoved(o.moved(row.name, choice.kind === 'user' ? choice.name : choice.group.name))
      setChoosing(false)
      void client.invalidateQueries({ queryKey: ['admin', 'marketplace', 'needs-owner'] })
    }
    if (row.kind === 'agent') moveAgent.mutate({ agentId: row.id, to }, { onSuccess: done })
    else moveBase.mutate({ baseId: row.id, to }, { onSuccess: done })
  }

  return (
    <StudioRow style={riseDelay(index)}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
        <Icon size={14} strokeWidth={2} aria-hidden className="shrink-0 text-fg-muted" />
        <h3 className="min-w-0 truncate text-[14px]">{row.name}</h3>
        <Badge>{o.kind[row.kind]}</Badge>
      </div>

      <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[12.5px] text-fg-muted">
        {row.owner.kind === 'user' ? (
          <span>{o.ownerLeft(row.owner.name)}</span>
        ) : (
          <>
            <span>{o.ownedBy}</span>
            <GroupLabel group={row.owner.group} size={16} to />
            <span>{o.groupUnmanaged}</span>
          </>
        )}
      </p>
      <p className="readout mt-0.5 text-fg-subtle">{o.since(formatAgo(row.since))}</p>

      {!choosing ? (
        <div className="mt-3 border-t border-rule pt-3">
          <Button size="sm" aria-label={o.chooseLabel(row.name)} onClick={() => setChoosing(true)}>
            {o.choose}
          </Button>
        </div>
      ) : (
        user && (
          <div className="mt-3 space-y-3 border-t border-rule pt-3">
            <OwnerPicker
              value={choice}
              onChange={setChoice}
              viewer={{ id: user.id, displayName: user.displayName, managedGroups: user.managedGroups }}
              current={current}
              allowAnyone
              disabled={pending}
            />
            {error && (
              <p role="alert" className="text-[12.5px] text-alert">
                {o.notMoved} {describeApiError(messages, error)}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="primary" disabled={pending || choice === null} onClick={submit}>
                {pending ? o.moving : o.move}
              </Button>
              <Button size="sm" variant="ghost" disabled={pending} onClick={() => setChoosing(false)}>
                {o.cancel}
              </Button>
            </div>
          </div>
        )
      )}
    </StudioRow>
  )
}
